import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Package, ShieldCheck, ArrowSquareOut, Info, Truck } from "@phosphor-icons/react";

const CARRIERS = [
  {
    id: "mondial_relay",
    name: "Mondial Relay",
    coverage: "France métropolitaine, Belgique, Luxembourg, Pays-Bas, Espagne",
    price: "À partir de 4,30 €",
    delay: "3–5 jours ouvrés",
    anonymity: "Anonyme : point relais → point relais (aucune adresse échangée)",
    color: "bg-rose-50 border-rose-200 text-rose-700",
    pillColor: "bg-rose-500",
    url: "https://www.mondialrelay.fr/envoi-de-colis/",
    note: "Recommandé pour la métropole. Idéal s'il y a un point relais près de chacun.",
  },
  {
    id: "colissimo",
    name: "Colissimo (La Poste)",
    coverage: "France métropolitaine + DOM-TOM + International",
    price: "À partir de 5,90 €",
    delay: "2–3 jours ouvrés",
    anonymity: "Anonyme avec « Point Retrait » uniquement (sinon l'adresse est visible)",
    color: "bg-orange-50 border-orange-200 text-orange-700",
    pillColor: "bg-orange-500",
    url: "https://www.laposte.fr/colissimo-en-ligne",
    note: "Recommandé pour les DOM-TOM ou si pas de Mondial Relay à proximité. Bien choisir l'option « Point Retrait » pour rester anonyme.",
  },
];

export default function RelaisPage() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [choice, setChoice] = useState(null);
  const finderToken = localStorage.getItem(`finder_token_${convId}`);

  useEffect(() => { (async () => {
    try {
      const url = finderToken ? `/finder/${finderToken}/conversation/${convId}` : `/conversations/${convId}`;
      const { data } = await api.get(url);
      setConv(data);
      if (data?.shipping_carrier) setChoice(data.shipping_carrier);
    } catch (e) { toast.error("Impossible de charger la conversation"); }
  })(); }, [convId, finderToken]);

  const confirmChoice = async (carrierId) => {
    setChoice(carrierId);
    try {
      await api.post("/relais/agree", {
        conversation_id: convId,
        carrier: carrierId,
        finder_token: finderToken || null,
      });
      toast.success("Choix enregistré et notifié dans le chat ✅");
      navigate(`/conversations/${convId}`);
    } catch {
      toast.error("Erreur lors de l'enregistrement du choix");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="relais-page">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800 flex items-center gap-3">
          <Truck size={36} weight="duotone" className="text-orange-500" /> Envoyer le doudou
        </h1>
        <p className="text-stone-600 mt-2">
          Choisissez ensemble (famille + la personne qui a trouvé le doudou) le transporteur. Une fois validé,
          la <strong>famille</strong> achète l'étiquette en ligne et la <strong>partage via le chat</strong>.
          La personne qui a trouvé le doudou imprime, dépose le colis dans le point relais associé.
          <strong> Aucune adresse personnelle n'est échangée.</strong>
        </p>

        {/* Protocol box */}
        <div className="mt-6 bg-rose-50 border-2 border-rose-100 rounded-3xl p-5">
          <h2 className="font-display text-lg font-bold text-stone-800 flex items-center gap-2">
            <Info size={20} weight="duotone" className="text-rose-500" /> Comment ça marche ?
          </h2>
          <ol className="mt-3 space-y-2 text-sm text-stone-700">
            <li><strong>1.</strong> Vous choisissez ensemble le transporteur ci-dessous (Mondial Relay ou Colissimo).</li>
            <li><strong>2.</strong> La <strong>famille</strong> achète une étiquette en ligne, en mode particulier (aucun compte pro requis).</li>
            <li><strong>3.</strong> La famille indique le point relais de retrait dans son département.</li>
            <li><strong>4.</strong> La famille télécharge l'étiquette PDF et la partage dans le chat.</li>
            <li><strong>5.</strong> La personne qui a trouvé le doudou imprime l'étiquette, emballe le doudou et le dépose dans un point relais proche.</li>
            <li><strong>6.</strong> Le doudou arrive au point relais de la famille sous 2 à 5 jours. 🎉</li>
          </ol>
          <p className="text-xs text-stone-500 mt-3 flex items-start gap-1.5">
            <ShieldCheck size={14} weight="duotone" className="text-emerald-500 mt-0.5 shrink-0" />
            <span>L'achat de l'étiquette est <strong>à la charge de la famille</strong>. Doudou Alert ne perçoit aucune commission. Aucune intégration externe : tout est géré chez le transporteur.</span>
          </p>
        </div>

        {/* Carriers */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {CARRIERS.map((c) => (
            <div key={c.id} className={`flex flex-col rounded-3xl border-2 p-6 ${c.color} ${choice === c.id ? "ring-4 ring-offset-2 ring-orange-300" : ""}`} data-testid={`carrier-${c.id}`}>
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-2xl ${c.pillColor} text-white grid place-items-center shrink-0`}>
                  <Package size={22} weight="bold" />
                </span>
                <h3 className="font-display text-xl font-black text-stone-800">{c.name}</h3>
              </div>
              <dl className="mt-4 space-y-1.5 text-sm text-stone-700">
                <div className="flex gap-2"><dt className="font-bold w-24 shrink-0">Couverture :</dt><dd className="flex-1">{c.coverage}</dd></div>
                <div className="flex gap-2"><dt className="font-bold w-24 shrink-0">Prix :</dt><dd className="flex-1">{c.price}</dd></div>
                <div className="flex gap-2"><dt className="font-bold w-24 shrink-0">Délai :</dt><dd className="flex-1">{c.delay}</dd></div>
                <div className="flex gap-2"><dt className="font-bold w-24 shrink-0">Anonymat :</dt><dd className="flex-1">{c.anonymity}</dd></div>
              </dl>
              <p className="text-xs text-stone-600 mt-3 italic">{c.note}</p>
              <div className="mt-auto pt-4 flex flex-col gap-2 items-stretch">
                <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`open-${c.id}`}>
                  <Button variant="outline" className="w-full rounded-full border-2 border-stone-300 bg-white font-bold py-4 text-sm whitespace-normal h-auto min-h-[44px]">
                    <ArrowSquareOut size={16} weight="bold" className="mr-1.5 shrink-0" /> Acheter l'étiquette
                  </Button>
                </a>
                <Button onClick={() => confirmChoice(c.id)} className={`w-full rounded-full text-white font-bold py-4 text-sm whitespace-normal h-auto min-h-[44px] ${c.pillColor} hover:opacity-90`} data-testid={`confirm-${c.id}`}>
                  Choisir {c.name.split(" ")[0]}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button variant="ghost" onClick={() => navigate(`/conversations/${convId}`)} className="text-stone-500" data-testid="back-to-chat">
            ← Retour au chat
          </Button>
        </div>
      </div>
    </Layout>
  );
}
